import { List, Map, Set } from "immutable";

import { Assignment } from "seneca-common/features/assignments/state";
import * as assignmentSelectors from "seneca-common/features/assignments/state/selectors/state/assignments/assignment";
import { composeSelector } from "seneca-common/utils/selectors/compose-selectors";

import QuestionStat from "../../../../models/QuestionStat";
import SectionStat, { SectionStatType } from "../../../../models/SectionStat";
import StudentAssignment from "../../../../models/StudentAssignment";
import * as questionStatSelectors from "./questionStat";
import * as sectionStatSelectors from "./sectionStat";

type SectionStatsType = Map<string, SectionStat>;
type QuestionStatsType = Map<string, QuestionStat>;

// Defined as a const outside of selectors to allow memoisation to work
// We lose type safety here, so always specify the return type on any function that returns this
const emptyMap = Map<string, any>();

function getAssignmentSectionStats(
  assignment: StudentAssignment
): SectionStatsType {
  return assignment.sectionStats || emptyMap;
}

export function getAssignmentQuestionStats(
  assignment: StudentAssignment
): QuestionStatsType {
  return assignment.questionStats || emptyMap;
}

export function getAssignmentTeacherName(
  assignment: StudentAssignment
): string | undefined {
  return assignment.teacherName;
}

export function getAssignmentAggregatedQuestionsMarks(
  assignment: StudentAssignment
) {
  const stats = getAssignmentQuestionStats(assignment);
  return stats.reduce(
    (acc, stat) => {
      acc.marksTotal += questionStatSelectors.getQuestionMarksTotal(stat) ?? 0;
      acc.marksReceived += questionStatSelectors.getQuestionMark(stat) ?? 0;
      return acc;
    },
    {
      marksTotal: 0,
      marksReceived: 0
    }
  );
}

export function getAssignmentUserId(
  assignment: StudentAssignment
): string | undefined {
  return assignment.userId;
}

function getSectionStat(sectionStats: SectionStatsType, sectionId: string) {
  return sectionStats.get(sectionId);
}

function getQuestionStat(questionStats: QuestionStatsType, questionId: string) {
  return questionStats.get(questionId);
}

type SectionId = string;
export const getAssignmentSectionStat: (
  arg0: StudentAssignment,
  arg1: SectionId
) => SectionStatType | null | undefined = composeSelector(
  getAssignmentSectionStats,
  getSectionStat
);

export const getAssignmentQuestionStat: (
  state: StudentAssignment,
  questionId: string
) => QuestionStat | undefined = composeSelector(
  getAssignmentQuestionStats,
  getQuestionStat
);

export function getAssignmentQuestionMark(
  assignment: StudentAssignment,
  questionId: string
) {
  return questionStatSelectors.getQuestionMark(
    getAssignmentQuestionStat(assignment, questionId)
  );
}

export function getAssignmentQuestionMarksTotal(
  assignment: StudentAssignment,
  questionId: string
) {
  return questionStatSelectors.getQuestionMarksTotal(
    getAssignmentQuestionStat(assignment, questionId)
  );
}

export function isAssignmentQuestionFlagged(
  assignment: StudentAssignment,
  questionId: string
) {
  return questionStatSelectors.isQuestionFlagged(
    getAssignmentQuestionStat(assignment, questionId)
  );
}

export function getAssignmentSectionBestScore(
  assignment: StudentAssignment,
  sectionId: string
): number | null | undefined {
  const sectionStat = getAssignmentSectionStat(assignment, sectionId);
  return sectionStat && sectionStatSelectors.getBestScore(sectionStat);
}

export function getAssignmentSectionTotalStudyTime(
  assignment: StudentAssignment,
  sectionId: string
): number | null | undefined {
  const sectionStat = getAssignmentSectionStat(assignment, sectionId);
  return sectionStat && sectionStatSelectors.getTotalStudyTime(sectionStat);
}

export function getAssignmentQuestionStudyTime(
  assignment: StudentAssignment,
  questionId: string
): number | null | undefined {
  const questionStat = getAssignmentQuestionStat(assignment, questionId);
  return (
    questionStat && questionStatSelectors.getQuestionStudyTime(questionStat)
  );
}

export function getAssignmentTotalStudyTime(
  assignment: StudentAssignment
): number {
  // TODO: get from BE from aggregated stats when we return it
  const studiedSectionIds = getStudiedSectionIds(assignment);
  const sectionTimes = studiedSectionIds.map(sectionId =>
    getAssignmentSectionTotalStudyTime(assignment, sectionId)
  );
  const studiedQuestionIds = getStudiedQuestionIds(assignment);
  const questionTimes = studiedQuestionIds.map(questionId =>
    getAssignmentQuestionStudyTime(assignment, questionId)
  );

  return [...sectionTimes, ...questionTimes].reduce((acc: number, val) => {
    if (!val) return acc;
    return acc + val;
  }, 0);
}

export function getNumberOfItemsAssigned(assignment: StudentAssignment) {
  const numOfSectionsAssigned =
    assignmentSelectors.getAssignmentNumberOfSections(assignment as Assignment);
  const numOfQuestionsAssigned = getAssignmentExamQuestionIds(assignment).size;
  return numOfSectionsAssigned + numOfQuestionsAssigned;
}

export function getNumberOfItemsStudied(assignment: StudentAssignment) {
  const numOfStudiedSections = getAssignmentSectionStats(assignment).size;
  const numOfStudiedQuestions = getAssignmentQuestionStats(assignment).size;
  return numOfStudiedSections + numOfStudiedQuestions;
}

export function getNumberOfUnstudiedItems(assignment: StudentAssignment) {
  const numberOfStudiedItems = getNumberOfItemsStudied(assignment);
  const numberOfAssignedItems = getNumberOfItemsAssigned(assignment);
  return numberOfAssignedItems - numberOfStudiedItems;
}

export function getNumberOfUnstudiedSections(assignment: StudentAssignment) {
  const numOfSectionsAssigned =
    assignmentSelectors.getAssignmentNumberOfSections(assignment as Assignment);
  const numOfStudiedSections = getAssignmentSectionStats(assignment).size;

  return numOfSectionsAssigned - numOfStudiedSections;
}

export function getAssignmentProgress(assignment: StudentAssignment): number {
  const numOfItemsAssigned = getNumberOfItemsAssigned(assignment);
  const numberOfItemsStudied = getNumberOfItemsStudied(assignment);

  return numOfItemsAssigned > 0
    ? (numberOfItemsStudied / numOfItemsAssigned) * 100
    : 0;
}

export function getStudiedSectionIds(
  assignment: StudentAssignment
): List<string> {
  return getAssignmentSectionStats(assignment).keySeq().toList();
}

export function getStudiedQuestionIds(
  assignment: StudentAssignment
): List<string> {
  return getAssignmentQuestionStats(assignment).keySeq().toList();
}

export function getNumberOfQuestionsCompleted(
  assignment: StudentAssignment
): number {
  return getStudiedQuestionIds(assignment).size;
}

export function allExamQuestionsCompleted(assignment: StudentAssignment) {
  const assignedQuestionIds = getAssignmentExamQuestionIds(assignment);
  if (assignedQuestionIds.size === 0) return false;
  const completedQuestionIds = getStudiedQuestionIds(assignment);
  return (
    Set(completedQuestionIds).intersect(Set(assignedQuestionIds)).size ===
    assignedQuestionIds.size
  );
}

export function allSectionsCompleted(assignment: StudentAssignment) {
  const assignedSectionIds = assignmentSelectors.getAssignmentSectionIds(
    assignment as Assignment
  );
  const completedSectionIds = getStudiedSectionIds(assignment);
  return (
    Set(completedSectionIds).intersect(Set(assignedSectionIds)).size ===
    assignedSectionIds.size
  );
}

export function sectionHasBeenStudied(
  assignment: StudentAssignment,
  sectionId: string
): boolean {
  return getStudiedSectionIds(assignment).includes(sectionId);
}

export function getNumberOfSectionsCompleted(
  assignment: StudentAssignment
): number {
  return getStudiedSectionIds(assignment).size;
}

export function getAssignmentExamQuestionIds(
  assignment: StudentAssignment
): List<string> {
  return assignmentSelectors.getAssignmentExamQuestionIds(
    assignment as Assignment
  );
}

export function assignmentIncludesExamQuestions(assignment: StudentAssignment) {
  return assignmentSelectors.assignmentIncludesExamQuestions(
    assignment as Assignment
  );
}

export function isLastSectionToFinishAssignment(
  assignment: StudentAssignment,
  sectionId: string
) {
  const assignedSectionIds = assignmentSelectors.getAssignmentSectionIds(
    assignment as Assignment
  );
  const studiedSectionIds = getStudiedSectionIds(assignment);
  const unstudiedSectionIds = assignedSectionIds.filter(
    (id: any) => !studiedSectionIds.contains(id)
  );

  const numberOfSectionsLeftToStudy = unstudiedSectionIds.size;
  return (
    numberOfSectionsLeftToStudy === 1 &&
    unstudiedSectionIds.get(0) === sectionId
  );
}

export {
  getAssignmentId,
  getClassId,
  getStartDate,
  getDueDate,
  getSpec,
  getName,
  getAssignmentCourseId as getCourseId,
  getAssignmentSectionIds as getSectionIds,
  getAssignmentNumberOfSections as getNumberOfSections
} from "seneca-common/features/assignments/state/selectors/state/assignments/assignment";
